import React from 'react';
import {
  ITeenReferralPlansFields,
  ITeenReferralPlanFields
} from '../../../types/contentful';
import { TeenReferralPlan, usePageDataContext } from '../../context/PageData';
import PictogramComponent from '~/components/AtomicComponents/Pictogram';

interface Props extends ITeenReferralPlansFields {
  sysId?: string;
}

export function TeenReferralPlans({ enabled, plans, title, sysId }: Props) {
  const { teenReferralData } = usePageDataContext();

  // Hide if not enabled or no plans data
  if (
    !enabled ||
    !plans ||
    plans.length === 0 ||
    !teenReferralData?.plans ||
    teenReferralData.plans.length === 0
  ) {
    return null;
  }

  // Helper function to get backend plan data by key
  const getBackendPlan = (key: string): TeenReferralPlan | undefined => {
    if (!key) return undefined;
    return teenReferralData.plans.find((bp) => bp.name === key);
  };

  return (
    <div
      className="block-container"
      data-eltype="teen-referral-plans"
      data-blockid={sysId}
      id={sysId}
      data-testid="teenReferralPlans"
    >
      <div className="mx-md w-full desktop:w-8/12">
        {title && (
          <p className="typography-title-2 mb-6 font-normal">{title}</p>
        )}

        {plans.map((plan) => (
          <PlanSection
            key={plan.fields.key}
            plan={plan.fields}
            backendPlan={getBackendPlan(plan.fields.key)}
          />
        ))}
      </div>
    </div>
  );
}

interface PlanSectionProps {
  plan: ITeenReferralPlanFields;
  backendPlan?: TeenReferralPlan;
}

function PlanSection({ plan, backendPlan }: PlanSectionProps) {
  const { pictogram, planName, description } = plan;
  const price = backendPlan?.price;

  return (
    <div className="relative mb-4 flex items-start gap-4 rounded-md bg-screen-surface p-6">
      <div className="flex flex-1 flex-col gap-2">
        <div className="mb-1 flex items-center gap-3">
          {pictogram && (
            <PictogramComponent inverse={true} {...pictogram.fields} />
          )}
          <p className="typography-title-1">{planName}</p>
        </div>
        {price && (
          <p className="typography-title-5 font-semibold">
            {price?.replace('USD ', '$')} / month
          </p>
        )}
        {description && (
          <p className="typography-body-2 font-light">{description}</p>
        )}
        {backendPlan?.show_badge && (
          <div className="typography-button-sm w-fit rounded-full bg-accent1-weak px-2 py-1">
            Recommended based on your child&apos;s interests
          </div>
        )}
      </div>
    </div>
  );
}
